/**
 * DuplicateMediaPanel
 *
 * Duplicate-media findings on a claim: which photo was seen before, where, and what
 * the reviewer concluded about it.
 *
 * Shaped to sit alongside the potential-duplicate callout and read as the same
 * kind of thing, because it is - "have we seen this before" is one question
 * whether the match was on a photograph or an email address.
 *
 * Two deliberate departures from a plain warning banner:
 *
 * 1. The photos are side by side. A finding that says "this is a duplicate"
 *    without showing you what it matched is not reviewable, it is just an
 *    instruction to go looking.
 * 2. Closing it records a JUDGEMENT, not an acknowledgement. The outcomes come
 *    from the tenant's catalogue, so the wording is theirs, and each carries a
 *    platform intent so a rename cannot break what the verdict meant.
 */

import { useEffect, useEffectEvent, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  ExternalLink,
  ImageOff,
  Maximize2,
  ScanSearch,
} from "lucide-react";
import { toast } from "sonner";

import { FileViewerDialog } from "@/shared/components/file-viewer-dialog";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Textarea } from "@/shared/components/ui/textarea";
import { cn } from "@/shared/lib/utils";

export interface ImageReviewOutcome {
  key: string;
  label: string;
  description?: string;
  intent: string;
  requires_note: boolean;
}

export interface ImageReviewFile {
  uuid?: string;
  file_name?: string;
  file_url?: string;
  requirement_key?: string;
  captured_at?: string;
}

export interface ImageReviewClaimCard {
  uuid: string;
  claim_number?: string;
  created_at?: string;
  customer_name?: string;
  product_name?: string;
}

export interface ImageReviewArchiveCard {
  reference?: string;
  recorded_outcome?: string;
  captured_on?: string;
  prior_system?: string;
}

/**
 * Where the matched image came from.
 *
 * A match against the imported historical library has no claim to open. Left
 * undistinguished it renders as a photo with a blank card, which reads as a
 * broken panel rather than as the nature of the record - so the origin is
 * explicit, and the backend supplies the sentence that explains it.
 */
export interface ImageReviewOrigin {
  kind: "claim" | "archive" | "unknown";
  label: string;
  rationale: string;
  claim: ImageReviewClaimCard | null;
  archive: ImageReviewArchiveCard | null;
}

export interface ImageReviewMatch {
  uuid: string;
  confidence: string;
  severity: string;
  status: string;
  resolution?: string;
  resolution_intent?: string;
  resolution_note?: string;
  reviewed: boolean;
  submitted_image: ImageReviewFile;
  matched_image: ImageReviewFile;
  matched_origin: ImageReviewOrigin;
  /** Same object as `matched_origin.claim`; kept for older callers. */
  matched_claim: ImageReviewClaimCard | null;
}

export interface ImageReview {
  total: number;
  unresolved: number;
  outcomes: ImageReviewOutcome[];
  matches: ImageReviewMatch[];
}

interface Props {
  review: ImageReview | null | undefined;
  /** Opens the read-only claim preview in place, as the duplicate callout does. */
  onPreviewClaim?: (claimUuid: string) => void;
  onResolve: (args: {
    matchUuid: string;
    outcome: string;
    note: string;
  }) => Promise<void>;
  canResolve?: boolean;
  isResolving?: boolean;
}

function ArchiveCard({ archive }: { archive: ImageReviewArchiveCard }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-0.5 rounded-[6px] bg-[var(--bg-subtle)] px-2.5 py-2">
      <span className="text-[11px] font-medium">
        {archive.reference
          ? t(
              "claims.mediaReview.archiveReference",
              "Reference {{reference}}",
              {
                reference: archive.reference,
              },
            )
          : t("claims.mediaReview.historicalRecord", "Historical record")}
      </span>
      {archive.captured_on ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {archive.captured_on}
        </span>
      ) : null}
      {archive.recorded_outcome ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {t(
            "claims.mediaReview.previouslyRecordedAs",
            "Previously recorded as {{outcome}}",
            { outcome: archive.recorded_outcome },
          )}
        </span>
      ) : null}
      {/* No link, and the reason for it said out loud. */}
      <span className="text-[11px] text-[var(--ink-3)]">
        {t(
          "claims.mediaReview.noClaimRecord",
          "No claim record in the platform",
        )}
      </span>
    </div>
  );
}

function ClaimCard({
  claim,
  onPreviewClaim,
}: {
  claim: ImageReviewClaimCard;
  onPreviewClaim?: (claimUuid: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-0.5 rounded-[6px] bg-[var(--bg-subtle)] px-2.5 py-2">
      {claim.customer_name ? (
        <span className="text-[12px] font-medium text-[var(--ink)]">
          {claim.customer_name}
        </span>
      ) : null}
      {claim.product_name ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {claim.product_name}
        </span>
      ) : null}
      {onPreviewClaim ? (
        <Button
          variant="link"
          onClick={() => onPreviewClaim(claim.uuid)}
          className="mt-1 inline-flex h-auto cursor-pointer items-center justify-start gap-1 p-0 text-[11px] hover:underline"
        >
          {claim.claim_number ||
            t("claims.mediaReview.viewClaim", "View claim")}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </Button>
      ) : claim.claim_number ? (
        <span className="mt-1 text-[11px] font-medium">
          {claim.claim_number}
        </span>
      ) : null}
    </div>
  );
}

function ImagePane({
  title,
  file,
  onEnlarge,
}: {
  title: string;
  file: ImageReviewFile;
  onEnlarge: (file: ImageReviewFile) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <span className="text-[10px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
        {title}
      </span>
      {/* Both panes are the same fixed height so the pair is genuinely
        comparable - a resized copy must not read as a different photo simply
        because it renders smaller. */}
      {file?.file_url ? (
        <Button
          variant="ghost"
          onClick={() => onEnlarge(file)}
          aria-label={t("claims.mediaReview.enlarge", "Enlarge {{name}}", {
            name: file.file_name ?? title,
          })}
          className="group relative flex min-h-[240px] flex-1 w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-0 hover:bg-[var(--bg-subtle)]"
        >
          <img
            src={file.file_url}
            alt={file.file_name ?? title}
            className="max-h-full max-w-full object-contain"
          />
          <span className="absolute right-2 bottom-2 flex h-6 w-6 items-center justify-center rounded-[4px] bg-[var(--bg)] text-[var(--ink-2)] opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-3 w-3" />
          </span>
        </Button>
      ) : (
        <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center gap-1 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]">
          <ImageOff className="h-4 w-4" />
          <span className="text-[11px]">
            {t("claims.mediaReview.imageUnavailable", "Image unavailable")}
          </span>
        </div>
      )}
      <span className="truncate text-[11px] text-[var(--ink-3)]">
        {file?.file_name ?? "-"}
      </span>
    </div>
  );
}

/**
 * One phrase naming what this photo matched, for the collapsed row.
 *
 * Reads from the origin so an archive hit never claims to be a claim the
 * reviewer could go and open.
 */
function describeWhere(match: ImageReviewMatch, t: TFunction): string {
  const origin = match.matched_origin;
  if (origin?.kind === "archive") {
    return origin.archive?.reference
      ? t(
          "claims.mediaReview.matchesLibraryRef",
          "matches the historical library ({{reference}})",
          { reference: origin.archive.reference },
        )
      : t(
          "claims.mediaReview.matchesLibrary",
          "matches the historical library",
        );
  }
  if (origin?.claim) {
    // Tenants that do not number their claims still need the row to identify
    // the other one, so fall back to the customer rather than a bare noun.
    const who = origin.claim.claim_number || origin.claim.customer_name;
    return who
      ? t(
          "claims.mediaReview.matchesClaimFrom",
          "matches an earlier claim from {{who}}",
          { who },
        )
      : t("claims.mediaReview.matchesClaim", "matches an earlier claim");
  }
  return t("claims.mediaReview.matchesImage", "matched an earlier image");
}

interface PhotoGroup {
  key: string;
  image: ImageReviewFile;
  matches: ImageReviewMatch[];
}

/**
 * One group per photo on this claim. A photo reused across many claims raises
 * one finding per earlier image, so a flat list repeats the same photo dozens
 * of times; the reviewer's question is about the photo, not each pair.
 * Groups keep the backend's closest-first order.
 */
function groupByPhoto(matches: ImageReviewMatch[]): PhotoGroup[] {
  const groups = new Map<string, PhotoGroup>();
  for (const m of matches) {
    const key = m.submitted_image?.uuid || m.uuid;
    const group = groups.get(key);
    if (group) group.matches.push(m);
    else groups.set(key, { key, image: m.submitted_image, matches: [m] });
  }
  return [...groups.values()];
}

/** Where a match came from, as a chip label; one chip per earlier claim. */
function sourceOf(
  match: ImageReviewMatch,
  t: TFunction,
): { key: string; label: string } | null {
  const origin = match.matched_origin;
  if (origin?.claim) {
    return {
      key: origin.claim.uuid,
      label:
        origin.claim.claim_number ||
        origin.claim.customer_name ||
        t("claims.mediaReview.viewClaim", "View claim"),
    };
  }
  if (origin?.archive) {
    const reference = origin.archive.reference;
    return {
      key: `archive:${reference ?? match.uuid}`,
      label:
        reference ||
        t("claims.mediaReview.historicalRecord", "Historical record"),
    };
  }
  return null;
}

const VISIBLE_SOURCES = 4;

type ReviewStatus = "pending" | "duplicate" | "unsure" | "cleared";

/**
 * A photo takes the first status any of its matches has. Pending comes first:
 * a photo still needs the reviewer until every match on it is decided.
 */
const STATUS_ORDER: ReviewStatus[] = [
  "pending",
  "duplicate",
  "unsure",
  "cleared",
];

const STATUS_TEXT: Record<ReviewStatus, string> = {
  pending: "text-[var(--status-warn)]",
  duplicate: "text-[var(--status-error)]",
  unsure: "text-[var(--status-info)]",
  cleared: "text-[var(--status-ok)]",
};

const STATUS_STRIPE: Record<ReviewStatus, string> = {
  pending: "border-l-[var(--status-warn)]",
  duplicate: "border-l-[var(--status-error)]",
  unsure: "border-l-[var(--status-info)]",
  cleared: "border-l-[var(--status-ok)]",
};

function statusLabel(status: ReviewStatus, t: TFunction): string {
  switch (status) {
    case "pending":
      return t("claims.mediaReview.status.pending", "To review");
    case "duplicate":
      return t("claims.mediaReview.status.duplicate", "Duplicate");
    case "unsure":
      return t("claims.mediaReview.status.unsure", "Unsure");
    case "cleared":
      return t("claims.mediaReview.status.cleared", "Cleared");
  }
}

/** Falls back to an icon when the image will not load, not a broken glyph. */
function Thumbnail({ url }: { url?: string }) {
  const [failed, setFailed] = useState(false);
  return url && !failed ? (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  ) : (
    <ImageOff className="h-4 w-4 text-[var(--ink-3)]" />
  );
}

export function DuplicateMediaPanel({
  review,
  onPreviewClaim,
  onResolve,
  canResolve = true,
  isResolving = false,
}: Props) {
  const { t } = useTranslation();
  // Held by uuid, not as an object, so a refetch after each save is picked up.
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  const [showReviewed, setShowReviewed] = useState(false);
  // Choices per match, so stepping away and back does not lose them.
  const [drafts, setDrafts] = useState<
    Record<string, { outcome: string; note: string }>
  >({});
  // Saved this session; counts as reviewed before the refetch lands.
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [enlarged, setEnlarged] = useState<ImageReviewFile | null>(null);
  // Photos whose full list of earlier claims is showing.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [listOpen, setListOpen] = useState(false);
  // null until the reviewer picks one: then "To review" while any is left.
  const [filter, setFilter] = useState<ReviewStatus | "all" | null>(null);

  const groups = groupByPhoto(review?.matches ?? []);
  // Flattened back in group order, so Prev/Next walk one photo at a time.
  const matches = groups.flatMap((g) => g.matches);
  const outcomes = review?.outcomes ?? [];
  const isReviewed = (m: ImageReviewMatch) => m.reviewed || saved.has(m.uuid);
  const toReview = matches.filter((m) => !isReviewed(m)).length;
  /** The outcome on a match, including one saved here before the refetch. */
  const resolutionOf = (m: ImageReviewMatch) =>
    saved.has(m.uuid)
      ? (drafts[m.uuid]?.outcome ?? m.resolution)
      : m.resolution;
  /** Bucketed by intent, not label, so a tenant's renamed outcome still sorts. */
  const matchStatus = (m: ImageReviewMatch): ReviewStatus => {
    if (!isReviewed(m)) return "pending";
    const intent =
      (!saved.has(m.uuid) && m.resolution_intent) ||
      outcomes.find((o) => o.key === resolutionOf(m))?.intent;
    if (intent === "CONFIRMED") return "duplicate";
    if (intent === "FALSE_POSITIVE" || intent === "BENIGN") return "cleared";
    return "unsure";
  };
  const groupStatus = (g: PhotoGroup): ReviewStatus =>
    STATUS_ORDER.find((s) => g.matches.some((m) => matchStatus(m) === s)) ??
    "unsure";
  const counts = Object.fromEntries(
    STATUS_ORDER.map((s) => [
      s,
      groups.filter((g) => groupStatus(g) === s).length,
    ]),
  ) as Record<ReviewStatus, number>;
  const activeFilter = filter ?? (counts.pending > 0 ? "pending" : "all");
  const visibleGroups =
    activeFilter === "all"
      ? groups
      : groups.filter((g) => groupStatus(g) === activeFilter);
  /** The first match still to review, else the first at all. */
  const entryOf = (list: ImageReviewMatch[]) =>
    list.find((m) => !isReviewed(m)) ?? list[0];

  // What Prev/Next walk through. The open match always stays in, so opening a
  // reviewed row still has a position.
  const queue = matches.filter(
    (m) => showReviewed || !isReviewed(m) || m.uuid === openUuid,
  );
  const index = queue.findIndex((m) => m.uuid === openUuid);
  const current = index >= 0 ? queue[index] : null;
  const photoIndex = current
    ? groups.findIndex((g) => g.matches.includes(current))
    : -1;

  const draft = current
    ? (drafts[current.uuid] ?? {
        outcome: current.resolution ?? "",
        note: current.resolution_note ?? "",
      })
    : { outcome: "", note: "" };
  const { outcome, note } = draft;
  const setDraft = (patch: Partial<typeof draft>) => {
    if (!current) return;
    setDrafts((prev) => ({ ...prev, [current.uuid]: { ...draft, ...patch } }));
  };
  const selected = outcomes.find((o) => o.key === outcome);
  const noteRequired = Boolean(selected?.requires_note);

  /** Where Save goes next: the following match still to review, wrapping round. */
  const nextAfterSave = (fromUuid: string) => {
    const i = matches.findIndex((m) => m.uuid === fromUuid);
    const ahead = matches.slice(i + 1);
    const candidates = showReviewed
      ? ahead
      : [...ahead, ...matches.slice(0, i)].filter((m) => !isReviewed(m));
    return candidates.find((m) => m.uuid !== fromUuid) ?? null;
  };

  const step = (delta: number) => {
    const target = queue[index + delta];
    if (target) setOpenUuid(target.uuid);
  };

  const firstToReview = matches.find((m) => !isReviewed(m));

  const submit = async () => {
    if (!current || isResolving) return;
    // The button stays enabled on an incomplete form; the click validates and
    // says what is missing, rather than leaving the reviewer hunting.
    if (!outcome) {
      toast.error(
        t(
          "claims.mediaReview.chooseOutcomeError",
          "Choose what you found before saving.",
        ),
      );
      return;
    }
    if (noteRequired && !note.trim()) {
      toast.error(
        t(
          "claims.mediaReview.noteRequiredError",
          "This outcome needs a short note.",
        ),
      );
      return;
    }
    await onResolve({ matchUuid: current.uuid, outcome, note });
    setSaved((prev) => new Set(prev).add(current.uuid));
    setOpenUuid(nextAfterSave(current.uuid)?.uuid ?? null);
  };

  // Arrows step, Cmd/Ctrl+Enter saves. On window, not the dialog: reaching the
  // last match disables Next, which drops focus to <body>. Arrows are left
  // alone while typing or choosing an outcome, and in any other dialog.
  // An Effect Event reads the latest state, so the listener is bound once per
  // open dialog rather than on every render.
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target !== document.body && !target.closest("[data-media-review]"))
      return;
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canResolve) void submit();
      return;
    }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (
      target.closest(
        "input, textarea, select, [contenteditable], [role=combobox], [role=listbox], [role=option]",
      )
    )
      return;
    e.preventDefault();
    step(e.key === "ArrowLeft" ? -1 : 1);
  });
  const listening = Boolean(current) && !enlarged;
  useEffect(() => {
    if (!listening) return;
    const handler = (e: KeyboardEvent) => onKey(e);
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [listening]);

  if (!review || matches.length === 0) return null;

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-subtle)] px-4 py-2.5">
        <div className="flex items-start gap-2">
          <ScanSearch className="mt-px h-3 w-3 shrink-0 text-[var(--status-warn)]" />
          <span className="text-[11px] text-[var(--ink-2)]">
            {/* Counts photos, not matches: one photo seen on 40 earlier claims
              is still one photo. */}
            {groups.length === 1
              ? t(
                  "claims.mediaReview.summaryOne",
                  "Evidence check - 1 photo on this claim has been seen before",
                )
              : t(
                  "claims.mediaReview.summaryMany",
                  "Evidence check - {{count}} photos on this claim have been seen before",
                  { count: groups.length },
                )}
            {/* How many are outstanding is on the filter tabs below. */}.{" "}
            {toReview === 0
              ? t("claims.mediaReview.allReviewed", "All reviewed.")
              : t(
                  "claims.mediaReview.compareBeforeDeciding",
                  "Compare them before deciding.",
                )}
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {firstToReview ? (
              <Button
                variant="outline"
                size="xs"
                onClick={() => setOpenUuid(firstToReview.uuid)}
                className="cursor-pointer border-[var(--status-warn)] text-[var(--status-warn)]"
              >
                {t("claims.mediaReview.reviewAll", "Review all")}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setListOpen((v) => !v)}
              aria-expanded={listOpen}
              aria-label={
                listOpen
                  ? t("claims.mediaReview.hidePhotos", "Hide photos")
                  : t("claims.mediaReview.showPhotos", "Show photos")
              }
              className="cursor-pointer text-[var(--ink-2)]"
            >
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  listOpen && "rotate-180",
                )}
              />
            </Button>
          </div>
        </div>

        {/* Shown collapsed too: the counts are the progress at a glance.
          Picking one opens the list on it. */}
        <Tabs
          value={activeFilter}
          onValueChange={(v: string) => {
            setFilter(v as ReviewStatus | "all");
            setListOpen(true);
          }}
          className="pl-5"
        >
          <TabsList variant="pill" className="h-auto w-fit flex-wrap">
            {STATUS_ORDER.filter((s) => s === "pending" || counts[s] > 0).map(
              (s) => (
                <TabsTrigger key={s} value={s}>
                  {statusLabel(s, t)}
                  <span
                    className={cn(
                      "ml-1 tabular-nums",
                      counts[s] > 0 && STATUS_TEXT[s],
                    )}
                  >
                    {counts[s]}
                  </span>
                </TabsTrigger>
              ),
            )}
            <TabsTrigger value="all">
              {t("claims.mediaReview.status.all", "All")}
              <span className="ml-1 tabular-nums">{groups.length}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Collapsed by default so a claim with dozens of findings does not
          push the record below the fold; open, it scrolls after ~5 photos. */}
        {listOpen ? (
          <div className="flex max-h-[264px] flex-col gap-2 overflow-y-auto pl-5 pr-1">
            {visibleGroups.length === 0 ? (
              <span className="text-[11px] text-[var(--ink-3)]">
                {t(
                  "claims.mediaReview.nothingInFilter",
                  "No photos in this view.",
                )}
              </span>
            ) : null}
            {visibleGroups.map((group) => {
              // Closest-first from the backend, so the first is the strongest.
              const best = group.matches[0];
              const open = entryOf(group.matches);
              const left = group.matches.filter((m) => !isReviewed(m)).length;
              const status = groupStatus(group);
              const resolutions = new Set(group.matches.map(resolutionOf));
              const [only] = resolutions;
              const sources = new Map<
                string,
                { label: string; matches: ImageReviewMatch[] }
              >();
              for (const m of group.matches) {
                const source = sourceOf(m, t);
                if (!source) continue;
                const entry = sources.get(source.key);
                if (entry) entry.matches.push(m);
                else
                  sources.set(source.key, {
                    label: source.label,
                    matches: [m],
                  });
              }
              const chips = [...sources].map(([key, v]) => ({ key, ...v }));
              const shown = expanded.has(group.key)
                ? chips
                : chips.slice(0, VISIBLE_SOURCES);
              return (
                <div
                  key={group.key}
                  className={cn(
                    "flex items-start gap-2.5 border-l-2 pl-2",
                    STATUS_STRIPE[status],
                    // Under "All", decided photos step back so open ones lead.
                    activeFilter === "all" &&
                      status !== "pending" &&
                      "opacity-60",
                  )}
                >
                  <Button
                    variant="ghost"
                    onClick={() => setOpenUuid(open.uuid)}
                    aria-label={group.image?.file_name ?? best.confidence}
                    className="h-10 w-10 shrink-0 cursor-pointer overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-0"
                  >
                    <Thumbnail url={group.image?.file_url} />
                  </Button>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={
                          best.severity === "FAIL" ? "destructive" : "secondary"
                        }
                        className="text-[10px]"
                      >
                        {best.confidence}
                      </Badge>
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {group.matches.length === 1
                          ? describeWhere(best, t)
                          : t(
                              "claims.mediaReview.groupMatches",
                              "matches {{count}} earlier images",
                              { count: group.matches.length },
                            )}
                      </span>
                      <Button
                        variant="link"
                        onClick={() => setOpenUuid(open.uuid)}
                        className="inline-flex h-auto cursor-pointer items-center gap-1 p-0 text-[11px] text-[var(--status-warn)] hover:underline"
                      >
                        {t("claims.mediaReview.compare", "Compare")}
                      </Button>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 text-[11px] font-medium",
                          STATUS_TEXT[status],
                        )}
                      >
                        <Circle className="h-1.5 w-1.5 fill-current" />
                        {status === "pending"
                          ? group.matches.length > 1
                            ? t(
                                "claims.mediaReview.leftOf",
                                "{{left}} of {{count}} to review",
                                { left, count: group.matches.length },
                              )
                            : statusLabel(status, t)
                          : // The tenant's own word when every match agrees.
                            (resolutions.size === 1 &&
                              outcomes.find((o) => o.key === only)?.label) ||
                            statusLabel(status, t)}
                      </span>
                    </div>
                    {group.matches.length > 1 && chips.length > 0 ? (
                      // One chip per earlier claim, each opening its first match.
                      <div className="flex flex-wrap items-center gap-1">
                        {shown.map((chip) => (
                          <Button
                            key={chip.key}
                            variant="outline"
                            size="xs"
                            onClick={() =>
                              setOpenUuid(entryOf(chip.matches).uuid)
                            }
                            className="h-5 cursor-pointer px-1.5 text-[10px] font-normal"
                          >
                            {chip.label}
                            {chip.matches.length > 1
                              ? ` (${chip.matches.length})`
                              : ""}
                          </Button>
                        ))}
                        {chips.length > shown.length ? (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() =>
                              setExpanded((prev) =>
                                new Set(prev).add(group.key),
                              )
                            }
                            className="h-5 cursor-pointer px-1.5 text-[10px] font-normal text-[var(--ink-3)]"
                          >
                            {t(
                              "claims.mediaReview.moreSources",
                              "+{{count}} more",
                              {
                                count: chips.length - shown.length,
                              },
                            )}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <Dialog
        open={Boolean(current)}
        onOpenChange={(next) => !next && setOpenUuid(null)}
      >
        {/* Same footprint as the file viewer. `sm:` is needed: the base
          DialogContent caps at sm:max-w-sm. */}
        <DialogContent
          className="h-[95vh] w-[95vw] sm:max-w-[95vw]"
          data-media-review
        >
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-baseline gap-x-2">
              {current?.confidence}
              {current && isReviewed(current) ? (
                // What was already decided, so a revisit is not a blank slate.
                <span
                  className={cn(
                    "text-[12px] font-medium",
                    STATUS_TEXT[matchStatus(current)],
                  )}
                >
                  {outcomes.find((o) => o.key === resolutionOf(current))
                    ?.label ?? statusLabel(matchStatus(current), t)}
                </span>
              ) : null}
              {groups.length > 1 && photoIndex >= 0 ? (
                <span className="text-[12px] font-normal text-[var(--ink-2)]">
                  {t(
                    "claims.mediaReview.photoPosition",
                    "Photo {{position}} of {{count}}",
                    { position: photoIndex + 1, count: groups.length },
                  )}
                </span>
              ) : null}
              <span className="text-[12px] font-normal text-[var(--ink-3)]">
                {t("claims.mediaReview.position", "{{position}} of {{count}}", {
                  position: index + 1,
                  count: queue.length,
                })}
                {" · "}
                {t("claims.mediaReview.toReview", "{{count}} to review", {
                  count: toReview,
                })}
              </span>
            </DialogTitle>
            <DialogDescription>
              {current?.matched_origin?.rationale ? (
                <>{current.matched_origin.rationale} </>
              ) : null}
              {t(
                "claims.mediaReview.instructions",
                "Compare the two, then record what you found. This never declines a claim on its own.",
              )}
            </DialogDescription>
          </DialogHeader>

          {current ? (
            // Photos take the height; the decision sits beside them so it is
            // never pushed below the fold. Scrolls on very short screens.
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto md:flex-row">
              <div className="flex min-h-0 min-w-0 flex-1 gap-4">
                <ImagePane
                  title={t("claims.mediaReview.onThisClaim", "On this claim")}
                  file={current.submitted_image}
                  onEnlarge={setEnlarged}
                />
                <ImagePane
                  title={
                    current.matched_origin?.label ??
                    t("claims.mediaReview.earlierClaim", "Earlier claim")
                  }
                  file={current.matched_image}
                  onEnlarge={setEnlarged}
                />
              </div>

              <div className="flex flex-col gap-4 md:w-[280px] md:shrink-0">
                {/* No heading: the photo beside it already names the origin. */}
                {current.matched_origin?.claim ? (
                  <ClaimCard
                    claim={current.matched_origin.claim}
                    onPreviewClaim={onPreviewClaim}
                  />
                ) : current.matched_origin?.archive ? (
                  <ArchiveCard archive={current.matched_origin.archive} />
                ) : null}

                {canResolve ? (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="image-review-outcome">
                      {t(
                        "claims.mediaReview.whatDidYouFind",
                        "What did you find?",
                      )}
                    </Label>
                    <Select
                      value={outcome}
                      onValueChange={(v: string | null) =>
                        setDraft({ outcome: v ?? "" })
                      }
                    >
                      <SelectTrigger id="image-review-outcome">
                        <SelectValue
                          placeholder={t(
                            "claims.mediaReview.chooseOutcome",
                            "Choose an outcome",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {outcomes.map((o) => (
                          <SelectItem key={o.key} value={o.key}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selected?.description ? (
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {selected.description}
                      </span>
                    ) : null}
                    <Textarea
                      value={note}
                      onChange={(e) => setDraft({ note: e.target.value })}
                      placeholder={
                        noteRequired
                          ? t(
                              "claims.mediaReview.noteRequiredPlaceholder",
                              "Required - what made you decide that?",
                            )
                          : t(
                              "claims.mediaReview.noteOptionalPlaceholder",
                              "Optional note",
                            )
                      }
                      rows={4}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <DialogFooter className="sm:items-center">
            <div className="flex items-center gap-2 sm:mr-auto">
              <Button
                variant="outline"
                onClick={() => step(-1)}
                disabled={index <= 0 || isResolving}
                className="cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                {t("claims.mediaReview.previous", "Previous")}
              </Button>
              <Button
                variant="outline"
                onClick={() => step(1)}
                disabled={index >= queue.length - 1 || isResolving}
                className="cursor-pointer"
              >
                {t("claims.mediaReview.next", "Next")}
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Label className="ml-2 flex cursor-pointer items-center gap-2 text-[12px] font-normal text-[var(--ink-2)]">
                <Checkbox
                  checked={showReviewed}
                  onCheckedChange={(v) => setShowReviewed(Boolean(v))}
                />
                {t("claims.mediaReview.showReviewed", "Show reviewed too")}
              </Label>
            </div>
            <Button variant="outline" onClick={() => setOpenUuid(null)}>
              {t("claims.mediaReview.close", "Close")}
            </Button>
            {canResolve && current ? (
              <Button onClick={submit} disabled={isResolving}>
                {nextAfterSave(current.uuid)
                  ? t("claims.mediaReview.saveAndNext", "Save & next")
                  : t("claims.mediaReview.saveOutcome", "Save outcome")}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FileViewerDialog
        file={
          enlarged?.file_url
            ? {
                file_uuid: enlarged.uuid,
                file_url: enlarged.file_url,
                file_name: enlarged.file_name ?? "",
              }
            : null
        }
        onOpenChange={(next) => !next && setEnlarged(null)}
      />
    </>
  );
}
